import * as React from "react";
import {
  Box,
  Typography,
  Button,
  Checkbox,
  Collapse,
  LinearProgress,
  alpha,
  useTheme,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Card from "../../common/Card";
import { tokens } from "../../../theme";
import { useBranding } from "../../../BrandingContext";
import { useNavigate } from "react-router-dom";
import { PRODUCT_CATEGORY } from "../../../constants/applicationConstants";

/* ------------------------------------------------------------------ */
/*  Data                                                              */
/* ------------------------------------------------------------------ */
interface SubCategory {
  id: string;
  label: string;
}

interface Category {
  id: string;
  label: string;
  items: SubCategory[];
}

const categories: Category[] = [
  {
    id: "advanced",
    label: "Advanced",
    items: [
      { id: "amnion_single", label: "Amnion Single Layer" },
      { id: "amnion_dual", label: "Amnion Dual Layer" },
      { id: "amnion_tri", label: "Amnion Tri Layer" },
      { id: "amnion_quad", label: "Amnion Quad Layer" },
      { id: "aca_tri", label: "ACA Tri-Layer" },
      { id: "full_thickness", label: "Full Thickness" },
      { id: "dermal_matrix", label: "Dermal Matrix" },
      { id: "platelet_based", label: "Platelet Based" },
    ],
  },
  {
    id: "conservative",
    label: "Conservative",
    items: [{ id: "collagen_kits", label: `${PRODUCT_CATEGORY.CONSERVATIVE} Kits` }],
  },
  {
    id: "standard",
    label: "Standard",
    items: [
      { id: "debridement", label: "Debridement Devices" },
      { id: "dressings", label: "Dressings" },
      { id: "compressions", label: "Compressions" },
      { id: "supplies", label: "Supplies" },
    ],
  },
  {
    id: "general",
    label: "General",
    items: [
      { id: "glps", label: "GLPs" },
      { id: "peptides", label: "Peptides" },
    ],
  },
];

/* ------------------------------------------------------------------ */
/*  Component                                                         */
/* ------------------------------------------------------------------ */
export default function SelectCareCategories() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const branding = useBranding();
  const navigate = useNavigate();

  const [expanded, setExpanded] = React.useState<string[]>(
    categories.map((c) => c.id),
  );
  const [selected, setSelected] = React.useState<string[]>([]);

  const logoIconSrc =
    theme.palette.mode === "dark"
      ? branding?.logoIconWhite || branding?.logoWhite
      : branding?.logoIcon ||
        branding?.logo ||
        require("../../../branding-assets/v3biomed/logo.png");

  /* ---- handlers ---- */
  const toggleExpand = (id: string) =>
    setExpanded((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const toggleItem = (id: string) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const toggleCategory = (cat: Category) => {};

  const isCategoryChecked = (cat: Category) =>
    cat.items.every((i) => selected.includes(i.id));

  const isCategoryIndeterminate = (cat: Category) =>
    cat.items.some((i) => selected.includes(i.id)) && !isCategoryChecked(cat);

  const handleComplete = () => {
    console.log("Selected categories:", selected);
  };

  const cbSx = {
    color: alpha("#14b8a6", 0.4),
    "&.Mui-checked": { color: "#14b8a6" },
    "&.MuiCheckbox-indeterminate": { color: "#14b8a6" },
    p: 0.5,
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        px: 2,
        py: 4,
      }}
    >
      <Box mb={3}>
        <Box
          component="img"
          src={logoIconSrc}
          alt="V3 Biomed"
          sx={{ height: 56, objectFit: "contain" }}
        />
      </Box>

      <Box sx={{ width: "100%", maxWidth: 520, mb: 4 }}>
        <Box display="flex" justifyContent="space-between" mb={0.5}>
          <Typography variant="body2" sx={{ color: "#9ca3af" }}>
            Step 4 of 5
          </Typography>
          <Typography variant="body2" sx={{ color: "#14b8a6" }}>
            80%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={80}
          sx={{
            height: 6,
            borderRadius: 3,
            backgroundColor: alpha("#14b8a6", 0.15),
            "& .MuiLinearProgress-bar": {
              borderRadius: 3,
              backgroundColor: "#14b8a6",
            },
          }}
        />
      </Box>

      <Card sx={{ width: "100%", maxWidth: 520 }}>
        <Typography
          variant="h4"
          fontWeight={600}
          textAlign="center"
          sx={{ color: colors.grey[100], mb: 0.5 }}
        >
          Step 4: Select Your Care Categories
        </Typography>
        <Typography textAlign="center" sx={{ color: "#9ca3af", mb: 3 }}>
          Choose the product categories you'd like to work with. You can always
          update these later.
        </Typography>
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{ color: "#14b8a6", mb: 1.5 }}
        >
          Care Categories
        </Typography>

        {categories.map((cat) => {
          const isOpen = expanded.includes(cat.id);

          return (
            <Box
              key={cat.id}
              sx={{
                border: `1px solid ${alpha("#14b8a6", 0.2)}`,
                borderRadius: 2,
                mb: 2,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  px: 2,
                  py: 1.25,
                  cursor: "pointer",
                  "&:hover": { backgroundColor: alpha("#14b8a6", 0.04) },
                }}
                onClick={() => toggleExpand(cat.id)}
              >
                <Checkbox
                  size="small"
                  checked={isCategoryChecked(cat)}
                  indeterminate={isCategoryIndeterminate(cat)}
                  onChange={(e) => {
                    e.stopPropagation();
                    toggleCategory(cat);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  sx={cbSx}
                />
                <Typography
                  fontWeight={700}
                  sx={{ flex: 1, ml: 1, color: colors.grey[100] }}
                >
                  {cat.label}
                </Typography>
                {isOpen ? (
                  <ExpandLessIcon sx={{ color: "#9ca3af" }} />
                ) : (
                  <ExpandMoreIcon sx={{ color: "#9ca3af" }} />
                )}
              </Box>

              <Collapse in={isOpen}>
                <Box sx={{ pl: 3, pr: 2, pb: 1.5 }}>
                  {cat.items.map((item) => (
                    <Box
                      key={item.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        py: 0.5,
                        cursor: "pointer",
                      }}
                      onClick={() => toggleItem(item.id)}
                    >
                      <Checkbox
                        size="small"
                        checked={selected.includes(item.id)}
                        onChange={() => toggleItem(item.id)}
                        onClick={(e) => e.stopPropagation()}
                        sx={cbSx}
                      />
                      <Typography
                        variant="body2"
                        sx={{ ml: 1, color: "#cbd5e1" }}
                      >
                        {item.label}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Collapse>
            </Box>
          );
        })}

        <Button
            fullWidth
            size="large"
            variant="contained"
            sx={{
              borderRadius: 1.5,
              backgroundColor:
                theme.palette.mode === "dark" ? "#169c9c" : "#c6ebeb",
              color: colors.grey[100],
              fontWeight: 500,
              textTransform: "none",
              "&:hover": {
                backgroundColor:
                  theme.palette.mode === "dark" ? "#169c9ce6" : "#b6e0e0",
              },
            }}
          >
            Complete Setup
          </Button>
      </Card>
    </Box>
  );
}
